import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatWibJam, formatWibMenit, type TimelineSegment } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Strip pratinjau di bawah transport (§D8, paket P4).
 *
 * **Satu berkas sprite per segmen, bukan satu berkas per menit.** Segmen 15
 * menit menghasilkan satu `.thumbs.webp` berisi 15 sel bersebelahan dalam satu
 * baris; yang ditampilkan di sini potongannya, lewat `background-position`.
 * Bedanya bukan selera: 11.520 permintaan per hari menjadi 768, dan berkas
 * kecil di NFS membayar ongkos tetap yang jauh lebih besar daripada isinya.
 *
 * **Kontraknya dihitung, bukan ditanyakan.** Tidak ada sidecar JSON dan tidak
 * ada kolom DB baru:
 *
 * - jumlah sel = `ceil(durationS / 60)`, dibatasi 60 (segmen sejam, §D2),
 * - sel ke-`i` ada di `x = i × 160`, `y = 0` — sprite selalu satu baris,
 * - `durationS` sudah ada di respons `/timeline` yang dipegang halaman ini.
 *
 * **Sel kosong bukan berarti tidak ada rekaman.** Segmen yang terpotong pendek
 * (recorder mati di tengah) tetap menghasilkan sel hitam di ekornya. Yang
 * menjawab "ada rekaman atau tidak" tetap pita segmen di timeline (§D8.1);
 * menyamakan keduanya adalah kesalahan paling mahal di halaman ini, dan strip
 * ini tidak boleh ikut membuatnya.
 *
 * ## Skalanya mengikuti timeline, bukan berjalan sendiri
 *
 * Sampai 8 September 2026 strip ini selalu satu sel = satu menit, lima belas
 * menit berturut-turut di sekitar playhead — apa pun yang sedang ditunjukkan
 * timeline di atasnya. Akibatnya paling terasa persis ketika strip paling
 * dibutuhkan: pada sumbu tujuh hari, timeline memperlihatkan seminggu penuh
 * sementara strip memperlihatkan seperempat jam, dan dua benda yang bertetangga
 * di layar itu berbicara tentang dua rentang waktu yang sama sekali berbeda.
 * Orangnya sendiri yang menemukannya: *"di mode singlecam, bisa nggak sih
 * thumbnails itu menyesuaikan besar rentang waktu/skala timeline-nya?"*
 *
 * ## Segmen yang masih ditulis bukan lagi lubang 15 menit
 *
 * Dilaporkan lapangan 8 September 2026: *"saat segment recording terakhir masih
 * ongoing dan file belum closed, thumbnail saat ini menjadi bolong/kosong sampai
 * segment selesai."* Sisi server sekarang merakit **lembar sementara** dari
 * bingkai tile yang sudah ada di memori worker (§D8.1), jadi ujung terbaru
 * punya pratayang sampai ±satu menit yang lalu.
 *
 * Yang dikerjakan di berkas ini dua hal yang menyusul dari situ:
 *
 * 1. **Geometri lembar datang dari server** (`spriteCells`), bukan dihitung
 *    dari `durationS`. Lembar sementara selalu selebar nominal segmen walau
 *    baru tiga menit terisi; menghitungnya dari durasi yang bertambah tiap
 *    menit akan membuat tiap sel menunjuk gambar yang salah.
 * 2. **Sel yang rekamannya ada tapi gambarnya belum jadi tidak boleh terlihat
 *    seperti "tidak ada rekaman".** Itu justru kesalahan paling mahal di
 *    halaman ini (§D8.1). Sel begitu diberi kotak berdenyut bertuliskan
 *    *"Membuat pratinjau…"* — bukan kotak abu-abu yang diam.
 *
 * ## Skala strip dipisah dari zoom timeline
 *
 * Diminta lapangan 8 September 2026 sore: *"pisahkan skala zoom timeline dengan
 * skala thumbnail strip. (…) Timeline boleh terus zoom-in, tetapi thumbnail
 * strip memiliki zoom floor / minimum time span."*
 *
 * Lantainya turunan langsung dari sumber datanya, bukan angka selera: sprite
 * disampel **satu bingkai per menit** dan tidak akan pernah lebih rapat, jadi
 * rentang tersempit yang masih bisa mengisi seluruh lebar layar adalah
 * `jumlahSel` menit. Timeline yang di-zoom ke 30 detik tetap melihat strip
 * ±15 menit; yang berubah cuma perannya — dari potret jendela jadi konteks di
 * sekitarnya — dan playhead strip yang menjaga keduanya tetap menunjuk jam yang
 * sama.
 *
 * Di atas lantai itu strip memotret **jendela yang sedang terlihat di timeline**, dan
 * jarak antar sel dipilih dari `TANGGA_MENIT` supaya jumlah selnya tetap wajar:
 * jendela 15 menit → satu sel per menit (persis kelakuan lama), jendela 6 jam →
 * satu sel per 30 menit, jendela seminggu → satu sel per 12 jam. Yang digambar
 * tetap sel sprite yang sungguh ada; sel yang mewakili sepotong waktu tanpa
 * rekaman memang tidak digambar sama sekali (§D8.1).
 */

/** Sama dengan `SPRITE_CELL_W`/`_H` di `backend/src/services/spriteThumb.ts`. */
const SEL_W = 160;
const SEL_H = 90;
/** Sama dengan `SEL_MAX` di sisi server — segmen sejam (§D2) = 60 sel. */
const SEL_MAX = 60;

/**
 * Jarak antar sel yang boleh dipilih, dalam menit.
 *
 * Tangga yang sama semangatnya dengan `TANGGA_MS` di timeline: yang dicari
 * angka yang wajar diucapkan orang ("tiap setengah jam"), bukan hasil bagi
 * seperti 4 menit 37 detik. Satu menit adalah lantainya karena itu resolusi
 * sprite yang sungguh ada di server — meminta lebih rapat tidak menghasilkan
 * gambar baru, cuma sel yang sama dua kali.
 */
const TANGGA_MENIT = [1, 2, 5, 10, 15, 30, 60, 120, 180, 360, 720, 1440];

/**
 * WIB tidak pernah punya DST dan tetap UTC+7 sejak 1964.
 *
 * Dipakai untuk **membariskan** sel pada jam yang bulat menurut jam dinding
 * setempat. Tanpa ini, `floor(ms / 12 jam)` membariskan sel pada 07:00 dan
 * 19:00 WIB — benar secara aritmetika, dan langsung terbaca sebagai salah oleh
 * siapa pun yang melihatnya.
 */
const WIB_OFFSET_MS = 7 * 3_600_000;

/**
 * Seberapa jauh ke belakang dari sekarang sebuah sel di segmen yang masih
 * ditulis dianggap **belum tentu sempat dirakit**.
 *
 * Harus ≥ `LENSA_SPRITE_LIVE_FLUSH_MS` di server (bawaan 60 detik) plus satu
 * menit, karena sel sebuah menit baru bisa ada sesudah menitnya lewat dan
 * sesudah flush berikutnya. Dua menit adalah janji yang bisa ditepati; angka
 * yang lebih kecil akan membuat sel yang memang belum ada terlihat sebagai sel
 * yang gagal.
 */
const AMBANG_UJUNG_MS = 120_000;

/** Lebar sel minimal supaya teks "Membuat pratinjau…" masih terbaca di dalamnya. */
const LEBAR_TEKS_MIN_PX = 92;

/** Jarak antar sel, harus sama dengan `gap-1.5` di kelas wadahnya. */
const JARAK_SEL_PX = 6;

/** Lebar sel yang enak dibaca. Dipakai membagi lebar wadah jadi jumlah sel. */
const LEBAR_SEL_PX = 116;
/** Batas jumlah sel: cukup untuk dipindai sekaligus, tidak sampai jadi bubur. */
const SEL_MIN_TAMPIL = 4;
const SEL_MAKS_TAMPIL = 15;

interface Sel {
  /** Kunci render: `{recordingId}:{indeks}`. */
  key: string;
  recordingId: string;
  /** Indeks sel di dalam sprite segmennya — inilah yang menggeser gambar. */
  indeks: number;
  /** Lebar sprite segmen ini dalam piksel, untuk `background-size`. */
  lebarSprite: number;
  /** Jam dinding awal menit ini. */
  atMs: number;
  /** Segmennya masih ditulis — lembarnya sementara dan bisa berubah. */
  terbuka: boolean;
  /**
   * Pembeda cache untuk segmen yang masih ditulis.
   *
   * Lembar sementara ditulis ulang tiap menit, jadi URL yang sama persis akan
   * menampilkan lembar yang sudah basi sampai peramban memutuskan bertanya.
   * `endedAt` bergerak setiap kali timeline disegarkan, jadi ia penanda versi
   * yang sudah tersedia tanpa menambah apa pun ke respons.
   */
  versi: string;
}

/**
 * Satu kotak yang digambar.
 *
 * `sel` boleh `null`, dan itu perubahan penting: petak tanpa rekaman **tetap
 * digambar** sebagai placeholder seukuran yang lain. Sebelumnya ia dilewati,
 * dan barisan yang berlubang itulah yang membuat sel sisanya melar.
 */
interface Petak {
  sel: Sel | null;
  /** Awal petak — inilah yang dituju kalau kotaknya diklik. */
  mulaiMs: number;
  akhirMs: number;
}

export function StripSprite({
  segments,
  positionMs,
  viewFromMs,
  viewToMs,
  batasMinMs,
  batasMaksMs,
  onSeek,
  onGeserJendela,
}: {
  segments: TimelineSegment[];
  positionMs: number | null;
  /**
   * Jendela yang sedang terlihat di timeline.
   *
   * **Bukan lagi jendela strip itu sendiri** (8 September 2026, sore) — ia
   * cuma acuan pusat dan lebar. Strip punya lantai rentangnya sendiri; lihat
   * catatan "Skala strip dipisah dari zoom timeline" di atas.
   */
  viewFromMs: number;
  viewToMs: number;
  /**
   * Batas keras sumbu waktu (lantai retensi … sekarang).
   *
   * Dipakai menggeser — bukan menyempitkan — jendela strip yang melewati ujung.
   * Tanpa ini, playhead di tepi terbaru membuat separuh strip menunjuk masa
   * depan, dan separuh strip berisi "tidak ada rekaman" yang benar tapi tidak
   * berguna.
   */
  batasMinMs: number;
  batasMaksMs: number;
  onSeek: (ms: number) => void;
  /**
   * Geser jendela timeline sekian milidetik.
   *
   * Tombol `‹`/`›` di kedua ujung dulu menggeser jendela **milik strip
   * sendiri**. Begitu strip mengikuti timeline, jendela itu tidak ada lagi:
   * satu-satunya jendela yang tersisa milik timeline, dan tombolnya menggeser
   * yang itu. Hasilnya justru lebih jujur — strip dan timeline tidak pernah
   * bisa lagi menunjuk dua tempat berbeda.
   */
  onGeserJendela: (deltaMs: number) => void;
}) {
  const { t } = useBahasa();

  /**
   * Seluruh menit yang ada rekamannya, urut.
   *
   * Dibangun dari segmen, bukan dari rentang jam: menit yang tidak terekam
   * memang tidak punya sel, dan strip yang menyisipkan kotak kosong untuk
   * bolong akan terbaca sebagai rekaman gelap.
   */
  const sel = useMemo<Sel[]>(() => {
    const out: Sel[] = [];
    for (const sg of segments) {
      const mulai = new Date(sg.startedAt).getTime();
      /**
       * Dua angka yang berbeda, dan memisahkannya inti perbaikan ini:
       *
       * - `lebar` = lebar lembar di disk (`spriteCells` dari server) → dipakai
       *   `background-size`. Untuk segmen yang masih ditulis ia **nominal** dan
       *   tidak berubah sepanjang umur segmen.
       * - `n` = berapa menit yang benar-benar sudah ada rekamannya → dipakai
       *   memutuskan berapa sel yang pantas digambar.
       *
       * `spriteCells` bisa tidak ada sebentar saat API lama masih melayani di
       * tengah deploy; jatuh balik ke perilaku lama supaya strip tidak rusak.
       */
      const lebar = Math.max(1, Math.min(SEL_MAX, sg.spriteCells || Math.ceil(sg.durationS / 60)));
      const n = Math.max(1, Math.min(lebar, Math.ceil(sg.durationS / 60)));
      for (let i = 0; i < n; i++) {
        out.push({
          key: `${sg.id}:${i}`,
          recordingId: sg.id,
          indeks: i,
          lebarSprite: lebar * SEL_W,
          atMs: mulai + i * 60_000,
          terbuka: !sg.isComplete,
          versi: sg.endedAt,
        });
      }
    }
    out.sort((a, b) => a.atMs - b.atMs);
    return out;
  }, [segments]);

  /**
   * URL lembar sebuah sel. Segmen yang masih ditulis membawa penanda versi.
   *
   * Tanpa penanda itu peramban memegang satu URL yang isinya berubah tiap
   * menit; rutenya memang menjawab `no-cache` sehingga ia akan bertanya, tapi
   * `background-image` yang URL-nya tidak berubah tidak selalu memicu
   * pertanyaan itu. Dengan penanda, lembar baru selalu terambil begitu timeline
   * disegarkan.
   */
  const urlLembar = useCallback(
    (c: Sel) =>
      c.terbuka
        ? `/api/v1/recordings/${c.recordingId}/sprite?v=${encodeURIComponent(c.versi)}`
        : `/api/v1/recordings/${c.recordingId}/sprite`,
    [],
  );

  /**
   * Lembar mana yang sudah benar-benar ada.
   *
   * Dipakai untuk membedakan **"gambarnya sedang dibuat"** dari **"tidak ada
   * rekaman"** — dan itu pembedaan yang §D8.1 sebut sebagai kesalahan paling
   * mahal di halaman ini kalau tertukar. Diperiksa dengan satu `Image()` per
   * lembar, bukan per sel: lima belas sel satu segmen memakai satu berkas yang
   * sama, dan peramban memakai ulang unduhan itu untuk `background-image`.
   */
  /**
   * Kuncinya `recordingId` saja, **bukan** `recordingId:versi` — dan itu bukan
   * penyederhanaan, itu perbaikan.
   *
   * Lembar segmen yang masih ditulis punya versi baru tiap kali timeline
   * disegarkan (tiap 60 detik). Kalau statusnya disimpan per versi, versi baru
   * selalu lahir sebagai "belum diketahui", dan seluruh sel segmen itu berkedip
   * jadi kotak berdenyut selama satu putaran jaringan — tiap menit, di bagian
   * layar yang justru sedang diperhatikan orang.
   *
   * Disimpan per segmen, status lamanya bertahan selama versi barunya dijemput.
   * Yang bisa salah: lembar yang tiba-tiba lenyap tetap terbaca "ada" sampai
   * probe berikutnya menjawab — dan itu jauh lebih murah daripada berkedip.
   */
  const [lembarAda, setLembarAda] = useState<Record<string, boolean>>({});

  /**
   * Berapa sel yang muat — diukur, bukan ditetapkan.
   *
   * Lima belas sel tetap adalah angka desktop: di ponsel selebar 360px ia
   * menghasilkan kotak 20px yang tidak memperlihatkan apa pun, dan strip
   * pratinjau yang tidak bisa dilihat cuma memakan tinggi layar yang justru
   * paling langka di sana.
   */
  const wadahRef = useRef<HTMLDivElement>(null);
  const [lebarWadah, setLebarWadah] = useState(0);
  useEffect(() => {
    const el = wadahRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setLebarWadah(entry?.contentRect.width ?? 0));
    ro.observe(el);
    setLebarWadah(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);

  /**
   * Berapa sel yang muat, dan **selebar apa** masing-masing.
   *
   * Keduanya dihitung dari lebar wadah saja — tidak satu pun dari jumlah
   * pratayang yang kebetulan tersedia. Itu inti perbaikan 8 September 2026
   * sore. Dilaporkan lapangan: *"ketika user melakukan scrub sampai ke area
   * yang thumbnail-nya belum tersedia atau habis, thumbnail yang sudah ada
   * malah membesar sendiri untuk mengisi ruang kosong."*
   *
   * Penyebabnya `flex-1` pada tiap sel: lima sel di baris yang muat dua belas
   * akan melar dua kali lipat. Sekarang lebarnya dipatok piksel, dan petak yang
   * tidak punya gambar tetap menempati ruangnya sendiri sebagai placeholder —
   * jadi tidak ada lagi keadaan di mana jumlah data mengubah ukuran gambar.
   */
  // Jaraknya ikut dihitung, kalau tidak `n × lebar` melebihi wadahnya dan sel
  // terakhir terpotong `overflow-hidden`.
  const muat = Math.floor((Math.max(1, lebarWadah) + JARAK_SEL_PX) / (LEBAR_SEL_PX + JARAK_SEL_PX));
  const jumlahSel = Math.max(SEL_MIN_TAMPIL, Math.min(SEL_MAKS_TAMPIL, muat || SEL_MIN_TAMPIL));
  const lebarSel = Math.max(
    1,
    Math.floor((Math.max(1, lebarWadah) - (jumlahSel - 1) * JARAK_SEL_PX) / jumlahSel),
  );

  /**
   * **Jendela strip, dengan lantainya sendiri.**
   *
   * Diminta lapangan 8 September 2026: *"pisahkan skala zoom timeline dengan
   * skala thumbnail strip. (…) Timeline boleh terus zoom-in, tetapi thumbnail
   * strip memiliki zoom floor / minimum time span."*
   *
   * Lantainya bukan angka selera, ia turunan langsung dari sumber datanya:
   * sprite disampel **satu bingkai per menit** dan tidak akan pernah lebih
   * rapat dari itu, jadi rentang tersempit yang masih bisa mengisi seluruh
   * lebar layar adalah `jumlahSel` menit. Meminta lebih sempit tidak
   * menghasilkan sel baru — ia cuma menyisakan ruang kosong di kanan, atau
   * (sebelum lebar sel dipatok) membuat dua-tiga sel melar sampai absurd.
   *
   * Di atas lantai itu strip mengikuti timeline apa adanya. Di bawahnya, ia
   * berhenti menyempit dan berubah peran: dari *potret jendela* jadi *konteks di
   * sekitar jendela*. Timeline dan playhead tetap mengikuti zoom sungguhan
   * orangnya — yang dibatasi cuma strip.
   *
   * Pusatnya titik tengah jendela timeline, bukan playhead: yang dilihat orang
   * adalah jendela itu, dan strip yang melompat mengikuti playhead saat orang
   * sedang menggulung ke tempat lain akan terasa lepas dari layarnya.
   */
  /**
   * Jarak antar sel: tangga **terbesar yang tidak melebihi** jatah per sel.
   *
   * Arahnya sengaja dibalik dari bentuk pertamanya (tangga terkecil yang ≥
   * target), dan itu memperbaiki cacat yang terlihat begitu strip dipisah dari
   * timeline: langkah yang lebih besar daripada jatah menghasilkan **lebih
   * sedikit** sel daripada yang muat di layar. Pada jendela 6 jam dengan 11 sel,
   * jatahnya 32,7 menit; tangga ke atas memilih 60 menit dan barisnya cuma
   * terisi enam — separuh layar kosong, yaitu keluhan yang sama persis.
   *
   * Ke bawah, barisnya selalu penuh: `jumlahSel × langkah ≤ rentang`, jadi strip
   * memuat paling banyak sebanyak yang muat dan tidak pernah menyisakan ruang.
   *
   * Lantainya satu menit — resolusi sprite yang benar-benar ada di server.
   * Meminta lebih rapat tidak menghasilkan gambar baru, cuma sel yang sama dua
   * kali.
   */
  const langkahMs = useMemo(() => {
    const jatahMenit = Math.max(60_000, viewToMs - viewFromMs) / jumlahSel / 60_000;
    let menit = TANGGA_MENIT[0]!;
    for (const m of TANGGA_MENIT) if (m <= jatahMenit) menit = m;
    return menit * 60_000;
  }, [viewFromMs, viewToMs, jumlahSel]);

  /**
   * Rentang strip = jumlah sel × langkahnya. **Bukan lebar jendela timeline.**
   *
   * Inilah pemisahan yang diminta: timeline boleh terus di-zoom, strip berhenti
   * di lantainya sendiri (`jumlahSel` menit) dan berubah peran — dari potret
   * jendela jadi konteks di sekitarnya.
   *
   * Pusatnya titik tengah jendela timeline, bukan playhead: yang dilihat orang
   * adalah jendela itu, dan strip yang melompat mengikuti playhead saat orang
   * sedang menggulung ke tempat lain akan terasa lepas dari layarnya.
   */
  const rentang = jumlahSel * langkahMs;
  const stripFrom = useMemo(() => {
    const pusat = (viewFromMs + viewToMs) / 2;
    let from = pusat - rentang / 2;
    // Digeser, bukan disempitkan: strip yang menyempit di tepi arsip akan
    // mengubah ukuran selnya, dan ukuran sel yang berubah-ubah justru yang
    // dikeluhkan sejak awal.
    if (from + rentang > batasMaksMs) from = batasMaksMs - rentang;
    if (from < batasMinMs) from = batasMinMs;
    return from;
  }, [viewFromMs, viewToMs, rentang, batasMinMs, batasMaksMs]);
  const stripTo = stripFrom + rentang;

  /**
   * Petak yang digambar.
   *
   * Sumbunya dibagi rata pada kelipatan `langkahMs` menurut jam WIB, lalu tiap
   * petak mengambil **sel pertama** yang jatuh di dalamnya. Petak tanpa sel
   * dilewati, bukan diisi kotak kosong — itu aturan §D8.1 yang sama.
   */
  const petak = useMemo<Petak[]>(() => {
    if (sel.length === 0) return [];
    const awal = Math.floor((stripFrom + WIB_OFFSET_MS) / langkahMs) * langkahMs - WIB_OFFSET_MS;
    const out: Petak[] = [];
    let i = 0;
    for (let m = awal; m < stripTo; m += langkahMs) {
      // Maju sampai sel pertama yang belum lewat dari petak ini.
      while (i < sel.length && sel[i]!.atMs < m) i++;
      const kandidat = sel[i];
      // Petak tanpa sel **tetap masuk**, dengan `sel: null`. Ia yang menjaga
      // lebar barisan tetap sama apa pun isinya, dan ia juga yang membuat
      // "tidak ada rekaman" punya wujudnya sendiri alih-alih diam-diam hilang.
      out.push({
        sel: kandidat && kandidat.atMs < m + langkahMs ? kandidat : null,
        mulaiMs: m,
        akhirMs: m + langkahMs,
      });
      if (out.length >= jumlahSel) break;
    }
    return out;
  }, [sel, stripFrom, stripTo, langkahMs, jumlahSel]);

  useEffect(() => {
    let batal = false;
    // Satu probe per (segmen, versi): lima belas sel memakai berkas yang sama,
    // dan peramban memakai ulang unduhan ini untuk `background-image`.
    const daftar = new Map<string, string>();
    // Hanya petak yang digambar (≤ SEL_MAKS_TAMPIL). Dulu semua segmen timeline
    // diprobe — 672 di sumbu 7 hari, 2.880 di 30 hari — dan ±59 % kena 429
    // plafon sprite (audit 13 Sep 2026).
    for (const p of petak) if (p.sel) daftar.set(`${p.sel.recordingId}:${p.sel.versi}`, urlLembar(p.sel));

    for (const [gabungan, url] of daftar) {
      const id = gabungan.slice(0, gabungan.indexOf(':'));
      const img = new Image();
      img.onload = () => {
        if (!batal) setLembarAda((m) => (m[id] === true ? m : { ...m, [id]: true }));
      };
      img.onerror = () => {
        if (!batal) setLembarAda((m) => (m[id] === false ? m : { ...m, [id]: false }));
      };
      img.src = url;
    }
    return () => {
      batal = true;
    };
  }, [petak, urlLembar]);

  /** Petak yang sedang diputar — pembandingnya petak, bukan menit. */
  const kunciAktif = useMemo(() => {
    if (positionMs === null) return null;
    const p = petak.find((c) => positionMs >= c.mulaiMs && positionMs < c.akhirMs);
    return p ? `${p.mulaiMs}` : null;
  }, [petak, positionMs]);

  /**
   * Label sel: jam:menit saja begitu langkahnya menit bulat.
   *
   * Detik pada sel yang mewakili setengah jam ("13:00:00") menjanjikan
   * ketepatan yang tidak dimiliki gambarnya.
   */
  const label = (ms: number) => (langkahMs < 60_000 ? formatWibJam(ms) : formatWibMenit(ms));

  // Wadah tetap dipasang walau kosong: ia yang diukur `ResizeObserver`, dan
  // melepasnya berarti lebarnya kembali 0 setiap kali rekamannya habis.
  return (
    <div className="flex shrink-0 items-center gap-1.5" data-kendali="">
      <button
        type="button"
        onClick={() => onGeserJendela(-rentang / 2)}
        aria-label={t('Earlier previews')}
        className="flex h-16 w-8 shrink-0 items-center justify-center rounded-md border border-line hover:bg-subtle disabled:opacity-30"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m14 6-6 6 6 6" />
        </svg>
      </button>

      <div ref={wadahRef} className="relative flex min-w-0 flex-1 gap-1.5 overflow-hidden">
        {/* Playhead strip — **jam yang sama dengan master timeline**, bukan
            sekadar sel yang sedang aktif.

            Wajib ada sejak strip punya lantai rentangnya sendiri: begitu
            timeline di-zoom lebih dekat daripada lantai itu, satu sel mewakili
            satu menit penuh sementara timeline mungkin cuma menampilkan tiga
            puluh detik. Cincin di sekeliling sel aktif menjawab "menit ini",
            dan itu tidak lagi cukup untuk menjawab "detik ini ada di sebelah
            mana". Garisnya yang menjawab.

            Dipetakan linier atas lebar wadah: tiap sel mewakili rentang yang
            sama besar dan jaraknya seragam, jadi pendekatan ini meleset paling
            banyak selebar satu celah — dan yang dijanjikan strip memang
            konteks, bukan ketepatan milidetik. */}
        {positionMs !== null && positionMs >= stripFrom && positionMs <= stripTo && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-red-600"
            style={{ left: `${((positionMs - stripFrom) / rentang) * 100}%` }}
          />
        )}
        {petak.length === 0 ? (
          <p className="flex h-16 w-full items-center justify-center rounded-md border border-dashed border-line text-xs text-muted">
            {t('No recording in this part of the timeline')}
          </p>
        ) : (
          petak.map((c) => {
            const aktif = kunciAktif === `${c.mulaiMs}`;
            const muatTeks = lebarSel >= LEBAR_TEKS_MIN_PX;

            /**
             * Tiga keadaan, dan ketiganya **berukuran sama**.
             *
             * Yang lama cuma punya dua — "ada gambar" dan "kotak abu-abu" — dan
             * kotak abu-abu itu memikul dua arti yang berbeda jauh: *belum
             * dibuat* dan *memang tidak ada rekamannya*. §D8.1 menyebut
             * tertukarnya keduanya sebagai kesalahan paling mahal di halaman
             * ini, jadi sekarang masing-masing punya wujudnya sendiri:
             *
             * - **ada gambar** → potongan lembar sprite;
             * - **sedang dibuat** → kotak berdenyut + *"Membuat pratinjau…"*;
             * - **tidak ada rekaman** → kotak diam bergaris putus-putus.
             *
             * Tidak satu pun berwarna hitam. Dilaporkan lapangan: *"kalau
             * memang tidak ada thumbnail atau thumbnail belum selesai dibuat,
             * jangan sampai menghasilkan blok hitam besar yang bikin timeline
             * terlihat seperti rekamannya putus atau rusak."*
             */
            const kosong = c.sel === null;
            const belumUjung =
              c.sel !== null && c.sel.terbuka && c.sel.atMs > Date.now() - AMBANG_UJUNG_MS;
            const menunggu =
              c.sel !== null && (lembarAda[c.sel.recordingId] !== true || belumUjung);

            return (
              <button
                key={c.mulaiMs}
                type="button"
                // Petak kosong pun bisa dituju: sejak playhead tidak lagi
                // dibulatkan ke segmen terdekat, berdiri di bolong rekaman
                // adalah posisi yang sah.
                onClick={() => onSeek(c.sel ? c.sel.atMs : c.mulaiMs)}
                title={formatWibJam(c.mulaiMs)}
                aria-label={
                  kosong
                    ? t('{jam} — no recording', { jam: formatWibJam(c.mulaiMs) })
                    : menunggu
                      ? t('{jam} — preview still being generated', { jam: formatWibJam(c.mulaiMs) })
                      : t('Jump to {jam}', { jam: formatWibJam(c.mulaiMs) })
                }
                aria-current={aktif || undefined}
                aria-busy={menunggu || undefined}
                // `shrink-0` + lebar piksel: inilah yang menahan sel supaya
                // tidak pernah melar mengisi ruang yang ditinggalkan sel lain.
                style={{ width: lebarSel }}
                className={`group relative shrink-0 overflow-hidden rounded-md ring-offset-1 ring-offset-page transition-[box-shadow] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
                  aktif ? 'ring-2 ring-accent' : 'ring-1 ring-line hover:ring-accent/60'
                }`}
              >
                {kosong ? (
                  <span className="flex aspect-video w-full items-center justify-center rounded-t-md border border-dashed border-line bg-page px-1 text-center">
                    {muatTeks && (
                      <span className="text-[10px] leading-tight text-muted">
                        {t('No recording')}
                      </span>
                    )}
                  </span>
                ) : menunggu ? (
                  /* Berdenyut, bukan diam: yang membedakan "sedang dikerjakan"
                     dari "memang kosong" adalah gerakan, dan itu satu-satunya
                     isyarat yang terbaca tanpa membaca teks. */
                  <span className="flex aspect-video w-full animate-pulse items-center justify-center bg-subtle px-1 text-center">
                    {muatTeks && (
                      <span className="text-[10px] leading-tight text-muted">
                        {t('Generating thumbnails…')}
                      </span>
                    )}
                  </span>
                ) : (
                  /* Gambarnya latar, bukan `<img>`: yang dipotong satu sel dari
                     lembar panjang, dan `object-fit` tidak bisa memotong offset.
                     `image-rendering` dibiarkan bawaan — sel 160×90 yang
                     direntang sedikit masih terbaca, dan yang dicari orang di
                     sini bentuk besar ("ramai atau sepi"), bukan detail. */
                  <span
                    className="block aspect-video w-full bg-subtle bg-no-repeat"
                    style={{
                      backgroundImage: `url(${urlLembar(c.sel!)})`,
                      backgroundSize: `${c.sel!.lebarSprite}px ${SEL_H}px`,
                      backgroundPosition: `-${c.sel!.indeks * SEL_W}px 0`,
                    }}
                  />
                )}
                <span className="block bg-surface py-0.5 text-center text-[11px] tabular-nums text-muted">
                  {label(c.mulaiMs)}
                </span>
              </button>
            );
          })
        )}
      </div>

      <button
        type="button"
        onClick={() => onGeserJendela(rentang / 2)}
        aria-label={t('Later previews')}
        className="flex h-16 w-8 shrink-0 items-center justify-center rounded-md border border-line hover:bg-subtle disabled:opacity-30"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m10 6 6 6-6 6" />
        </svg>
      </button>
    </div>
  );
}
